import * as React from 'react';
import { SessionExpiredDialog } from './SessionExpiredDialog';
import { SessionWarningDialog } from './SessionWarningDialog';
import { getTotalRemainingTime } from './SessionHelper';
import { getCookie } from 'sos-common/js/utils/helpers';

interface ISessionHandlerProps {
  loginUrl: string;
  cookieName: string;
  renewSessionUrl: string;
  sessionDialogText: string;
  sessionDialogHeader: string;
  sessionIngoreButtonText: string;
  sessionRenewButtonText: string;
  sessionWarningTime: number;
}

interface ISessionHandlerState {
  hasShownExpiredDialog: boolean;
  hasShownWarningDialog: boolean;
  isExpiredDialogOpen: boolean;
  isWarningDialogOpen: boolean;
  sessionExpirationTime?: Date;
}

export class SessionHandler extends React.Component<ISessionHandlerProps, ISessionHandlerState> {
  interval?: ReturnType<typeof setInterval>;

  constructor(props) {
    super(props);

    this.state = {
      hasShownExpiredDialog: false,
      hasShownWarningDialog: false,
      isExpiredDialogOpen: false,
      isWarningDialogOpen: false,
      sessionExpirationTime: undefined,
    };
  }

  componentDidMount(): void {
    this.updateSessionDialogs();
    this.interval = setInterval(() => {
      this.updateSessionDialogs();
    }, 5000);
  }

  componentWillUnmount(): void {
    clearInterval(this.interval);
  }

  updateSessionDialogs = () => {
    const sessionTimeExpireCookie = getCookie(this.props.cookieName);

    if (!sessionTimeExpireCookie) {
      this.setState((prevState) => ({
        sessionExpirationTime: undefined,
        hasShownExpiredDialog: true,
        hasShownWarningDialog: true,
        isExpiredDialogOpen: prevState.isExpiredDialogOpen,
        isWarningDialogOpen: false,
      }));
      return;
    }

    const sessionExpirationTime = new Date(decodeURIComponent(sessionTimeExpireCookie));
    const timeRemaining = getTotalRemainingTime(sessionExpirationTime);

    this.setState((prevState) => {
      const nextState: ISessionHandlerState = {
        ...prevState,
        sessionExpirationTime,
      };

      if (timeRemaining <= 0) {
        if (!prevState.hasShownExpiredDialog) {
          this.clearSessionCookies();
          nextState.hasShownExpiredDialog = true;
          nextState.isExpiredDialogOpen = true;
        }

        nextState.hasShownWarningDialog = true;
        nextState.isWarningDialogOpen = false;
        return nextState;
      }

      if (prevState.hasShownExpiredDialog) {
        nextState.hasShownExpiredDialog = false;
        nextState.isExpiredDialogOpen = false;
      }

      if (timeRemaining <= this.props.sessionWarningTime && !prevState.hasShownWarningDialog) {
        nextState.hasShownWarningDialog = true;
        nextState.isWarningDialogOpen = true;
      } else if (timeRemaining > this.props.sessionWarningTime && prevState.hasShownWarningDialog) {
        nextState.hasShownWarningDialog = false;
        nextState.isWarningDialogOpen = false;
      }

      return nextState;
    });
  };

  closeExpiredDialog = () => {
    this.setState({
      isExpiredDialogOpen: false,
    });
  };

  closeWarningDialog = () => {
    this.setState({
      isWarningDialogOpen: false,
    });
  };

  deleteCookie = (name, path, domain) => {
    document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=' + path + ';domain=' + domain + ';';
  };

  // Hack från F5:an för att rensa sessionskaka som gör att siten inte går att nå om sessionen har gått ut medan man står på siten
  // Rensar alla * cookies
  clearSessionCookies = () => {
    var cookies = document.cookie.split(';');
    var domain = window.location.hostname;
    // Iterate through all cookies
    for (var i = 0; i < cookies.length; i++) {
      var cookie = cookies[i];
      var eqPos = cookie.indexOf('=');
      var name = eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim();

      // Delete the cookie for the current domain
      this.deleteCookie(name, '/', domain);

      // Split domain parts for wildcard domain cookie deletion
      var domainParts = domain.split('.');
      if (domainParts.length > 2) {
        //  deleting the cookie for wildcard domains
        for (var j = 2; j <= domainParts.length; j++) {
          var wildcardDomain = domainParts.slice(-j).join('.');
          this.deleteCookie(name, '/', '.' + wildcardDomain);
        }
      }
    }
  };

  render(): JSX.Element {
    return (
      <>
        <SessionExpiredDialog
          isOpen={this.state.isExpiredDialogOpen}
          loginUrl={this.props.loginUrl}
          onClose={this.closeExpiredDialog}
        />
        <SessionWarningDialog
          isOpen={this.state.isWarningDialogOpen}
          onClose={this.closeWarningDialog}
          renewSessionUrl={this.props.renewSessionUrl}
          sessionExpirationTime={this.state.sessionExpirationTime}
          translation={{
            sessionDialogHeader: this.props.sessionDialogHeader,
            sessionDialogText: this.props.sessionDialogText,
            sessionIngoreButtonText: this.props.sessionIngoreButtonText,
            sessionRenewButtonText: this.props.sessionRenewButtonText,
          }}
        />
      </>
    );
  }
}
